import { useEffect, useState } from 'react';
import { Alert, Card, Col, Empty, Row, Spin, Typography } from 'antd';
import { getGraficas } from '../api/participantesApi';

const TALLAS = ['Extrachica', 'Chica', 'Mediana', 'Grande', 'Extragrande'];
const prepararDatos = (datos) => {
  const cantidades = datos && typeof datos === 'object' ? datos : {};
  return [...TALLAS, ...Object.keys(cantidades).filter(talla => !TALLAS.includes(talla))].map(talla => {
    const valor = Number(cantidades[talla] ?? 0);
    return { talla, cantidad: Number.isFinite(valor) ? Math.max(0, valor) : 0 };
  });
};

const GraficaBarras = ({ titulo, datos, color, maximo }) => {
  const total = datos.reduce((suma, dato) => suma + dato.cantidad, 0);
  const ancho = Math.max(560, datos.length * 100 + 60);
  const espacio = (ancho - 70) / datos.length;
  return (
    <Card title={titulo} extra={`Total: ${total}`}>
      {total === 0 ? <Empty description="No hay playeras registradas" /> : (
        <div style={{ overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${ancho} 345`} role="img"
            aria-label={`${titulo}. ${datos.map(d => `${d.talla}: ${d.cantidad}`).join(', ')}. Total: ${total}.`}
            style={{ width: '100%', minWidth: ancho, display: 'block' }}>
            <text x="50" y="18" fontSize="12" fill="#595959">Cantidad de playeras</text>
            {[0, 1, 2, 3, 4].map(indice => {
              const y = 285 - 60 * indice;
              return <g key={indice}>
                <line x1="50" y1={y} x2={ancho - 10} y2={y} stroke="#e8e8e8" />
                <text x="40" y={y + 4} textAnchor="end" fontSize="12" fill="#595959">{maximo / 4 * indice}</text>
              </g>;
            })}
            {datos.map(({ talla, cantidad }, indice) => {
              const altura = cantidad / maximo * 240;
              const centro = 50 + espacio * (indice + 0.5);
              return <g key={talla}>
                <title>{`${talla}: ${cantidad} playeras`}</title>
                <rect x={centro - 25} y={285 - altura} width="50" height={altura} rx="4" fill={color} />
                <text x={centro} y={276 - altura} textAnchor="middle" fontSize="14" fontWeight="600">{cantidad}</text>
                <text x={centro} y="309" textAnchor="middle" fontSize="12" fill="#595959">{talla}</text>
              </g>;
            })}
            <text x={ancho / 2} y="337" textAnchor="middle" fontSize="12" fill="#595959">Talla</text>
          </svg>
        </div>
      )}
    </Card>
  );
};

const GraficaPersonas = ({ personas }) => {
  const datos = (Array.isArray(personas) ? personas : []).map(persona => {
    const cantidad = Number(persona.total_participantes ?? 0);
    return {
      categoria: persona.categoria_formateada || 'Sin categoría',
      cantidad: Number.isFinite(cantidad) ? Math.max(0, cantidad) : 0
    };
  });
  const total = datos.reduce((suma, dato) => suma + dato.cantidad, 0);
  const maximo = Math.max(1, ...datos.map(dato => dato.cantidad));
  const alto = datos.length * 52 + 50;

  return (
    <Card title="Participantes por categoría" extra={`Total: ${total}`}>
      {total === 0 ? <Empty description="No hay participantes registrados" /> : (
        <div style={{ overflowX: 'auto' }}>
          <svg viewBox={`0 0 900 ${alto}`} role="img"
            aria-label={`Participantes por categoría. ${datos.map(dato => `${dato.categoria}: ${dato.cantidad}`).join(', ')}. Total: ${total}.`}
            style={{ width: '100%', minWidth: 700, display: 'block' }}>
            {datos.map(({ categoria, cantidad }, indice) => {
              const y = indice * 52 + 10;
              const ancho = cantidad / maximo * 510;
              return (
                <g key={`${categoria}-${indice}`}>
                  <title>{`${categoria}: ${cantidad} participantes`}</title>
                  <text x="320" y={y + 21} textAnchor="end" fontSize="14" fill="#595959">{categoria}</text>
                  <rect x="335" y={y} width={ancho} height="32" rx="4" fill="#52c41a" />
                  <text x={345 + ancho} y={y + 21} fontSize="14" fontWeight="600">{cantidad}</text>
                </g>
              );
            })}
            <text x="590" y={alto - 8} textAnchor="middle" fontSize="12" fill="#595959">Cantidad de participantes</text>
          </svg>
        </div>
      )}
    </Card>
  );
};

const Graficas = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [respuesta, setRespuesta] = useState(null);
  useEffect(() => {
    let activo = true;
    const obtenerGraficas = async () => {
      try {
        const resultado = await getGraficas();
        if (!activo) return;
        if (resultado?.success && resultado.data) setRespuesta(resultado);
        else setError(resultado?.message || 'No se pudieron obtener las gráficas');
      } catch (err) {
        if (activo) setError(err.response?.data?.message || err.message || 'Fallo al conectar con el servidor');
      } finally {
        if (activo) setLoading(false);
      }
    };
    obtenerGraficas();
    return () => { activo = false; };
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /><p>Cargando gráficas...</p></div>;
  if (error) return <Alert title="Error al cargar las gráficas" description={error} type="error" showIcon />;
  const mujeres = prepararDatos(respuesta?.data?.playeram);
  const hombres = prepararDatos(respuesta?.data?.playerah);
  const mayorCantidad = Math.max(1, ...mujeres.map(d => d.cantidad), ...hombres.map(d => d.cantidad));
  const maximo = Math.ceil(mayorCantidad / 4) * 4;
  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={2}>Gráficas de participantes y playeras</Typography.Title>
      {respuesta?.carrera && <Typography.Paragraph type="secondary">{respuesta.carrera.nombre} · {respuesta.carrera.anio}</Typography.Paragraph>}
      <Row gutter={[24, 24]}>
        <Col span={24}><GraficaPersonas personas={respuesta?.data?.personas} /></Col>
        <Col xs={24} xl={12}><GraficaBarras titulo="Playeras de mujer" datos={mujeres} color="#eb2f96" maximo={maximo} /></Col>
        <Col xs={24} xl={12}><GraficaBarras titulo="Playeras de hombre" datos={hombres} color="#1677ff" maximo={maximo} /></Col>
      </Row>
    </div>
  );
};
export default Graficas;
